import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { MuiColorInput } from "mui-color-input";
import {
  FontDownload,
  Delete,
  Gradient,
  Image,
  VideoFile,
  CloudUpload,
} from "@mui/icons-material";

// Styled Components
export const StyledDropzone = styled("div")(({ theme }) => ({
  border: "2px dashed #ccc",
  borderRadius: "8px",
  padding: "40px",
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: "#f8f9fa",
  transition: "all 0.3s",
  "&:hover": {
    backgroundColor: "#e9ecef",
    borderColor: theme.palette.primary.main,
  },
}));

export const PreviewContainer = styled(Box)({
  position: "relative",
  height: 300,
  backgroundColor: "#f5f7fa",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
  overflow: "hidden",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const StyledCard = ({ children, title, icon: Icon }) => (
  <Card sx={{ height: "100%", boxShadow: 2, borderRadius: 2 }}>
    <CardContent
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      {title && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            {Icon && <Icon sx={{ mr: 1, color: "primary.main" }} />}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
        </>
      )}
      {children}
    </CardContent>
  </Card>
);

// Shadow Settings Component
export const ShadowSettings = ({ title, data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleNumberChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    handleChange(field, numValue);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Shadow Angle"
            type="number"
            value={data.shadowAngle || 45}
            onChange={(e) => handleNumberChange("shadowAngle", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Shadow Size"
            type="number"
            value={data.shadowSize || 5}
            onChange={(e) => handleNumberChange("shadowSize", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Shadow Distance"
            type="number"
            value={data.shadowDistance || 2}
            onChange={(e) =>
              handleNumberChange("shadowDistance", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Shadow Alpha"
            type="number"
            value={data.shadowAlpha || 0.3}
            onChange={(e) => handleNumberChange("shadowAlpha", e.target.value)}
            inputProps={{ min: 0, max: 1, step: 0.1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <MuiColorInput
            label="Shadow Color"
            format="hex"
            value={data.shadowColor || "#000000"}
            onChange={(value) => handleChange("shadowColor", value)}
            fullWidth
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// Gradient Settings Component
export const GradientSettings = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleNumberChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    handleChange(field, numValue);
  };

  const handleColorChange = (index, value) => {
    const newColors = [...(data.colors || [])];
    newColors[index] = value;
    onChange({ ...data, colors: newColors });
  };

  const addColor = () => {
    onChange({ ...data, colors: [...(data.colors || []), "#000000"] });
  };

  const removeColor = (index) => {
    const newColors = (data.colors || []).filter((_, i) => i !== index);
    onChange({ ...data, colors: newColors });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Gradient sx={{ mr: 1 }} /> Gradient Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={data.enabled}
                onChange={(e) => handleChange("enabled", e.target.checked)}
              />
            }
            label="Enable Gradient"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Gradient Angle"
            type="number"
            value={data.angle || 90}
            onChange={(e) => handleNumberChange("angle", e.target.value)}
            disabled={!data.enabled}
            InputProps={{ endAdornment: "°" }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Gradient Colors
          </Typography>
          {(data.colors || []).map((color, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={10}>
                <MuiColorInput
                  label={`Color ${index + 1}`}
                  format="hex"
                  value={color}
                  onChange={(value) => handleColorChange(index, value)}
                  fullWidth
                  disabled={!data.enabled}
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  onClick={() => removeColor(index)}
                  disabled={!data.enabled || (data.colors || []).length <= 1}
                  sx={{ mt: 1 }}
                >
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            onClick={addColor}
            disabled={!data.enabled}
            sx={{ mt: 1 }}
            variant="outlined"
            size="small"
          >
            Add Color
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
